export const APP = {
  name: process.env.REACT_APP_NAME || 'MyTownly Rides',
  /** Shown in Profile legal footer (Play Store compliance). */
  version: process.env.REACT_APP_VERSION || '1.0.94',
  privacyUrl: process.env.REACT_APP_PRIVACY_URL || '/privacy',
  termsUrl: process.env.REACT_APP_TERMS_URL || '/terms',
  city: process.env.REACT_APP_CITY || 'Piler',
  state: process.env.REACT_APP_STATE || 'Andhra Pradesh',
  mapCenter: {
    lat: Number(process.env.REACT_APP_MAP_CENTER_LAT) || 13.6549,
    lng: Number(process.env.REACT_APP_MAP_CENTER_LNG) || 78.9471,
  },
  /** Service area: Piler town + nearby (km) */
  serviceRadiusKm: Number(process.env.REACT_APP_SERVICE_RADIUS_KM) || 5,
  /**
   * Extra towns beyond the default city/mapCenter/serviceRadiusKm above
   * (config/platform.zones — see rideSecurity.js resolveZone, the
   * server-authoritative version of this same list). Same fare everywhere:
   * a zone only ever changes WHERE a passenger can book, never the price.
   */
  zones: [],
  /**
   * Which zone (by id) city/mapCenter/serviceRadiusKm above currently
   * reflect. 'default' until a real GPS fix resolves the passenger into a
   * named zone — see services/maps.js applyDetectedZone, called once Book
   * gets its first location fix on open.
   */
  currentZoneId: 'default',
  currentZoneActive: true,
  // An explicit town choice is a passenger preference. Background GPS must
  // never replace it; only the passenger's "Use my location" action may.
  zoneManuallySelected: false,
  /** How far from pickup to show nearby captains on map */
  nearbyCaptainRadiusKm: Number(process.env.REACT_APP_NEARBY_CAPTAIN_KM) || 4,
  /** Ignore captain GPS older than this (seconds) */
  captainLocationMaxAgeSec: Number(process.env.REACT_APP_CAPTAIN_LOC_MAX_AGE_SEC) || 180,
  /** Toast when captain is this many minutes from pickup */
  captainArrivingEtaMin: Number(process.env.REACT_APP_CAPTAIN_ARRIVING_ETA_MIN) || 3,
  /** Toast when captain is within this distance (metres) of pickup */
  captainArrivingDistanceM: Number(process.env.REACT_APP_CAPTAIN_ARRIVING_DIST_M) || 400,
  supportPhone: process.env.REACT_APP_SUPPORT_PHONE || '7989020222',
  supportWhatsapp: process.env.REACT_APP_SUPPORT_WHATSAPP || '917989020222',
  fare: {
    base: 30,
    perKm: 15,
    minimum: 30,
  },
  /** Operating mode from config/platform: 'normal' | 'paused' | 'emergency'. */
  mode: 'normal',
  /** Admin message shown when a passenger tries to book while paused/stopped. */
  modeMessage: '',
  bookingEnabled: true,
};

const TOWN_KEY = 'mt_rides_selected_town';

const initialNumber = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0 ? n : fallback;
};

// The default zone has to stay independent from APP.mapCenter. APP is pointed
// at whichever town the passenger selected, while this value remains Pileru's
// live, admin-configured zone.
let defaultZone = {
  id: 'default',
  name: APP.city,
  center: {
    lat: initialNumber(process.env.REACT_APP_MAP_CENTER_LAT, 13.6549),
    lng: initialNumber(process.env.REACT_APP_MAP_CENTER_LNG, 78.9471),
  },
  radiusKm: initialNumber(process.env.REACT_APP_SERVICE_RADIUS_KM, 5),
  active: true,
};

export const getDefaultZone = () => defaultZone;

export const setDefaultZoneFromConfig = (data = {}) => {
  const lat = Number(data.mapCenterLat);
  const lng = Number(data.mapCenterLng);
  const radiusKm = Number(data.serviceRadiusKm);
  defaultZone = {
    id: 'default',
    name: typeof data.city === 'string' && data.city.trim() ? data.city.trim() : defaultZone.name,
    center: {
      lat: Number.isFinite(lat) ? lat : defaultZone.center.lat,
      lng: Number.isFinite(lng) ? lng : defaultZone.center.lng,
    },
    radiusKm: Number.isFinite(radiusKm) && radiusKm > 0 ? radiusKm : defaultZone.radiusKm,
    active: true,
  };
  return defaultZone;
};

export const saveSelectedTownId = (id) => {
  try { localStorage.setItem(TOWN_KEY, id); } catch { /* private mode / full storage */ }
};

export const readSelectedTownId = () => {
  try { return localStorage.getItem(TOWN_KEY) || null; } catch { return null; }
};

export const clearSelectedTownId = () => {
  try { localStorage.removeItem(TOWN_KEY); } catch { /* private mode */ }
};

/**
 * Live config from the Firestore config/platform doc (admin Settings). This is
 * the SINGLE source — Firebase Remote Config was removed so fare / mode never
 * disagree between two systems.
 */
let remote = null;

export const applyRemoteConfig = (data) => {
  if (!data) {
    remote = null;
    return;
  }
  remote = data;
  setDefaultZoneFromConfig(data);
  if (data.state) APP.state = data.state;
  // city/mapCenter/serviceRadiusKm reflect whichever zone is CURRENTLY shown
  // (see currentZoneId above) — once a GPS fix has resolved the passenger
  // into a named zone, an admin editing the default zone's settings
  // elsewhere must not silently drag their screen back to it.
  if (APP.currentZoneId === 'default') {
    if (data.city) APP.city = data.city;
    if (data.mapCenterLat != null) APP.mapCenter.lat = Number(data.mapCenterLat);
    if (data.mapCenterLng != null) APP.mapCenter.lng = Number(data.mapCenterLng);
    if (data.serviceRadiusKm != null) APP.serviceRadiusKm = Number(data.serviceRadiusKm);
  }
  if (Array.isArray(data.zones)) {
    APP.zones = data.zones
      .filter((z) => z && typeof z === 'object' && z.id && z.center)
      .map((z) => ({
        id: String(z.id),
        name: (typeof z.name === 'string' && z.name.trim()) || String(z.id),
        center: { lat: Number(z.center.lat), lng: Number(z.center.lng) },
        radiusKm: Number(z.radiusKm),
        active: z.active !== false,
      }))
      .filter((z) => Number.isFinite(z.center.lat) && Number.isFinite(z.center.lng) && z.radiusKm > 0);
  } else {
    APP.zones = [];
  }
  if (data.nearbyCaptainRadiusKm != null) APP.nearbyCaptainRadiusKm = Number(data.nearbyCaptainRadiusKm);
  if (data.captainLocationMaxAgeSec != null) APP.captainLocationMaxAgeSec = Number(data.captainLocationMaxAgeSec);
  if (data.supportPhone != null) APP.supportPhone = String(data.supportPhone);
  if (data.supportWhatsapp != null) APP.supportWhatsapp = String(data.supportWhatsapp);

  const mode = ['normal', 'paused', 'emergency'].includes(data.mode)
    ? data.mode
    : (data.bookingEnabled === false ? 'paused' : 'normal');
  APP.mode = mode;
  APP.modeMessage = typeof data.modeMessage === 'string' ? data.modeMessage : '';
  APP.bookingEnabled = mode === 'normal';

  // A fare field left blank in the admin panel can arrive as null / '' — and
  // Number(null) is 0, which would silently collapse the estimate. Fall back to
  // the current value unless it's a real, non-negative number. The server
  // (calcFare in rideSecurity.js) applies the same rule so the pre-booking
  // estimate and the charged fare stay in step.
  const fareField = (v, fallback) => {
    if (v == null || v === '') return fallback;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };
  APP.fare = {
    base: fareField(data.fareBase, APP.fare.base),
    perKm: fareField(data.farePerKm, APP.fare.perKm),
    minimum: fareField(data.fareMinimum, APP.fare.minimum),
  };
};

/** Open Google Maps turn-by-turn to a lat/lng (passenger navigation FAB). */
export const openNavigation = (lat, lng) => {
  if (lat == null || lng == null) return;
  const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const calcFare = (distanceKm) => getFareParts(distanceKm).fare;

/** Honest fare lines for UI (base + distance; then minimum floor). */
export const getFareParts = (distanceKm, overrideTotal = null) => {
  const { base, perKm, minimum } = APP.fare;
  const distNum = Number(distanceKm) || 0;
  const rawSubtotal = Math.round(base + distNum * perKm);
  const totalNum = overrideTotal != null && Number.isFinite(Number(overrideTotal))
    ? Number(overrideTotal)
    : Math.max(minimum, rawSubtotal);
  const fare = totalNum;

  // Guarantee base + distanceCharge = fare (e.g. 30 + 62 = 92)
  const distanceCharge = Math.max(0, fare - base);
  return {
    base,
    perKm,
    distanceCharge,
    subtotal: fare,
    fare,
    appliedMinimum: rawSubtotal < minimum,
  };
};
